import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Company } from '../models/company';

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private companiesCache: Company[] = [];
  private companiesRequest$: Observable<Company[]> | null = null;

  constructor(private http: HttpClient) {}

  getCompanies(): Observable<Company[]> {
    if (this.companiesCache.length > 0) {
      return of(this.companiesCache);
    }
    if (!this.companiesRequest$) {
      this.companiesRequest$ = this.http.get<Company[]>(`${environment.apiUrl}companies`).pipe(
        tap((data) => { this.companiesCache = data; }),
        shareReplay(1)
      );
    }
    return this.companiesRequest$;
  }

  getCompanyName(companyId: string | undefined | null, companies: Company[]): string {
    if (!companyId) return '';
    return companies.find(c => c.id === companyId)?.name ?? '';
  }

  addCompany(company: { name: string; type: string; domains: string; billTo: string; billingInfo: string }): Observable<Company> {
    const url = `${environment.apiUrl}companies`;
    return this.http.post<Company>(url, company).pipe(
      tap((created) => {
        if (created) {
          this.companiesCache = [...this.companiesCache, created];
          this.companiesRequest$ = null;
        }
      })
    );
  }

  patchCompanyField(id: string, field: string, value: any): Observable<Company> {
    const url = `${environment.apiUrl}companies/${id}`;
    const body = { fieldName: field, newValue: value };
    return this.http.patch<Company>(url, body).pipe(
      tap((updated) => {
        const idx = this.companiesCache.findIndex(c => c.id === id);
        if (idx >= 0 && updated) {
          this.companiesCache[idx] = updated;
        }
      })
    );
  }

  deleteCompany(id: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}companies/${id}`).pipe(
      tap(() => {
        this.companiesCache = this.companiesCache.filter(c => c.id !== id);
        this.companiesRequest$ = null;
      })
    );
  }
}
